import { FLAGS } from '@iheartjane/feature-flags';
import { Button, SearchField, SearchIcon } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { useEffect, useRef } from 'react';
import { Form, useMatch } from 'react-router';

import { StoreSwitcher } from '@jane/bloom/components/storeSwitcher';
import { StoreSwitcherToggle } from '@jane/bloom/components/storeSwitcher/storeSwitcherToggle';
import { useOptimisticBagSize } from '@jane/bloom/hooks/useBagSize';
import { useDetectKeyboard } from '@jane/bloom/hooks/useDetectKeyboard';
import { useMainSiteUrl } from '@jane/bloom/hooks/useMainSiteUrl';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useScrollLock } from '@jane/bloom/hooks/useScrollLock';
import { useUserSearchQuery } from '@jane/bloom/hooks/useUserSearchQuery';
import { trackHeaderTap } from '@jane/bloom/services/tracking/track';
import { useIsDraft } from '@jane/bloom/utils/queryParams/isDraft';
import { SEARCH_TERM_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';

import { BloomLink } from './bloomLink';
import styles from './header.module.css';
import { AccountInfo } from './header/accountInfo';
import { CartInfo } from './header/cartInfo';
import { MainMenu } from './header/mainMenu';
import { SearchSuggestions } from './header/searchSuggestions';
import { SearchDropdown } from './search/searchDropdown';
import { SearchFullscreen } from './search/searchFullscreen';

type HeaderProps = {
  borderless?: boolean;
  /** A header sign-in should chain into the post-sign-in bank-linking prompt. */
  canPromptBankLinking?: boolean;
  dismissBankLinkingPrompt?: () => void;
  dismissLoginPrompt?: () => void;
  showLoginPrompt?: boolean;
};

export function Header({
  borderless = false,
  canPromptBankLinking = false,
  dismissBankLinkingPrompt,
  dismissLoginPrompt,
  showLoginPrompt = false,
}: HeaderProps) {
  const headerRef = useRef<HTMLElement>(null);
  const isDraft = useIsDraft();
  const {
    featureFlags,
    linkToMainSite,
    logo,
    pinnedStore,
    shoppingPreferences,
    theme,
  } = useRootLoaderData();
  const bagSize = useOptimisticBagSize();
  const useCustomComponents = featureFlags?.[FLAGS.c2CustomComponents] ?? false;
  const {
    hideSearchSuggestions,
    searchModalQuery,
    setSearchModalQuery,
    shouldOpenSearchModal,
    showSearchSuggestions,
    transition,
  } = useUserSearchQuery();
  const { setScrollLock } = useScrollLock();
  const isCheckout = useMatch('/checkout/*');
  const hasMainMenuItems =
    theme.bloom?.main_menu && theme.bloom?.main_menu.length > 0;
  const mainSiteUrl = useMainSiteUrl();

  useEffect(() => {
    setScrollLock(shouldOpenSearchModal);
  }, [setScrollLock, shouldOpenSearchModal]);

  const isKeyboardOpen = useDetectKeyboard({
    disableDetection: !isCheckout,
    interval: 100,
  });

  useEffect(() => {
    /**
     * Mobile Safari adds whitespace to the bottom of the page when the virtual keyboard is open
     * It results in the header scrolling off the page and being inaccesible when the keyboard closes
     * If we are in checkout, scroll the header into view when the keyboard closes
     */
    if (!isKeyboardOpen && isCheckout) {
      headerRef.current?.scrollIntoView?.({ behavior: 'smooth' });
    }
  }, [isKeyboardOpen, isCheckout]);

  const commonLinkProps = {
    'aria-label': `${pinnedStore.name} home`,
    className: clsx('focus-visible', styles['storeLogoLink']),
    onClick: () =>
      trackHeaderTap(
        {
          reservationMode: shoppingPreferences?.reservationMode,
          tappable: 'logo',
        },
        featureFlags
      ),
  };
  const logoImage = logo?.small && (
    <img
      height={48}
      loading="eager"
      src={logo.small}
      alt={`${pinnedStore.name} logo`}
      className={styles['storeLogo']}
    />
  );

  return (
    <>
      <header
        className={clsx(styles['header'], {
          [styles['header--borderless']]: borderless,
        })}
        ref={headerRef}
      >
        <div className={styles['headerContent']}>
          {linkToMainSite ? (
            <a {...commonLinkProps} href={mainSiteUrl}>
              {logoImage}
            </a>
          ) : (
            <BloomLink {...commonLinkProps} to="/">
              {logoImage}
            </BloomLink>
          )}

          <div
            className={clsx({
              [styles['storeSwitcher--desktop']]: !isCheckout,
            })}
          >
            {/*
              `id="store-name"` is attached only to the desktop instance.
              The toggle is rendered twice (desktop + mobile, swapped via CSS)
              so attaching the id to both would produce a duplicate id in the DOM.
              The desktop instance is rendered first, so getElementById finds it
              regardless of viewport.
            */}
            <StoreSwitcherToggle
              hidden={!!isCheckout}
              storeNameId="store-name"
            />
          </div>

          <div className={styles['searchForm']}>
            {useCustomComponents ? (
              <>
                <SearchDropdown />
                <SearchFullscreen />
              </>
            ) : (
              <>
                <Form
                  action="/search"
                  method="get"
                  name="Search Form"
                  onSubmit={() => hideSearchSuggestions()}
                >
                  <SearchField
                    className={styles['searchField']}
                    label="Search"
                    placeholder={`Search ${pinnedStore.name}`}
                    name={SEARCH_TERM_QUERY_KEY}
                    onChange={(val) => setSearchModalQuery(val)}
                    isDebounced={false}
                    borderRadius="lg"
                    value={searchModalQuery}
                    disableMobileInputStyling
                    onFocus={() => {
                      showSearchSuggestions();
                      trackHeaderTap(
                        {
                          reservationMode: shoppingPreferences?.reservationMode,
                          tappable: 'search icon',
                        },
                        featureFlags
                      );
                    }}
                    onBlur={() => hideSearchSuggestions()}
                  />
                </Form>
                <SearchSuggestions
                  hideSearchSuggestions={hideSearchSuggestions}
                  placeholder={`Search ${pinnedStore.name}`}
                  searchModalQuery={searchModalQuery}
                  setSearchModalQuery={setSearchModalQuery}
                  transition={transition}
                />
              </>
            )}
          </div>

          <div className={styles['icons']}>
            <Button.Icon
              ariaLabel="Search"
              className={styles['searchIcon']}
              icon={
                <SearchIcon className={styles['searchIcon']} color="inherit" />
              }
              onClick={() => {
                showSearchSuggestions();
                trackHeaderTap(
                  {
                    reservationMode: shoppingPreferences?.reservationMode,
                    tappable: 'search icon',
                  },
                  featureFlags
                );
              }}
            />
            <AccountInfo
              canPromptBankLinking={canPromptBankLinking}
              disabled={isDraft}
              dismissBankLinkingPrompt={dismissBankLinkingPrompt}
              {...(showLoginPrompt && dismissLoginPrompt
                ? {
                    dismissPrompt: dismissLoginPrompt,
                    showPrompt: true,
                  }
                : {})}
            />
            <CartInfo count={bagSize} disabled={isDraft} />
            {hasMainMenuItems && <MainMenu />}
          </div>
          <StoreSwitcher />
        </div>
      </header>
      <div
        className={clsx({
          [styles['storeSwitcher--mobile']]: !isCheckout,
        })}
      >
        <StoreSwitcherToggle hidden={!!isCheckout} />
      </div>
    </>
  );
}
